import { useRef, useState } from 'react'
import { Download } from 'lucide-react'
import { Button } from 'components/ds/Button'
import { DateRange, DateRangePicker } from 'components/ds/DateRangePicker'
import { IconButton } from 'components/ds/IconButton'
import { Popover } from 'components/ds/Popover'
import { SegmentedControl } from 'components/ds/SegmentedControl'
import { useSnackbar } from 'contexts/SnackbarContext'
import { downloadBlob } from 'utils/download'
import { SnackbarType } from 'utils/snackbar'
import { downloadSessionsExport, EXPORT_MAX_RANGE_DAYS, exportErrorMessage, SessionsExportFormat } from '../services/dispatchHelpApi'

// The server keys the range on UTC calendar days — send the picked calendar date, not an ISO instant.
const fmtDay = (d: Date) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`

const yesterday = (): Date => {
    const d = new Date()
    d.setDate(d.getDate() - 1)
    return d
}

/** Download the range's sessions as a file: JSONL (full transcripts, e.g. to feed to Claude) or a
 *  CSV summary row per session. Server-side it's the operator-gated /dispatch-help/export. */
export function ExportMenu() {
    const anchorRef = useRef<HTMLButtonElement>(null)
    const [open, setOpen] = useState(false)
    const [range, setRange] = useState<DateRange>(() => ({ start: yesterday(), end: yesterday() }))
    const [format, setFormat] = useState<SessionsExportFormat>('jsonl')
    const [downloading, setDownloading] = useState(false)
    const { showSnackbar } = useSnackbar()

    async function onExport() {
        if (!range.start || downloading) return
        const start = range.start
        const end = range.end ?? range.start
        // Mirror the server's cap before firing (Math.round absorbs DST-shifted local midnights).
        const dayCount = Math.round((end.getTime() - start.getTime()) / (24 * 60 * 60 * 1000)) + 1
        if (dayCount > EXPORT_MAX_RANGE_DAYS) {
            showSnackbar(SnackbarType.ERROR, `Export is limited to ${EXPORT_MAX_RANGE_DAYS} days — pick a shorter range`)
            return
        }
        const from = fmtDay(start)
        const to = fmtDay(end)
        setDownloading(true)
        try {
            const blob = await downloadSessionsExport(from, to, format)
            downloadBlob(blob, `ai-dispatch-sessions_${from}_${to}.${format}`)
            setOpen(false)
        } catch (error) {
            showSnackbar(SnackbarType.ERROR, (await exportErrorMessage(error)) ?? 'Export failed — try again or narrow the range')
        } finally {
            setDownloading(false)
        }
    }

    return (
        <>
            <IconButton
                ref={anchorRef}
                size="md"
                icon={<Download className="h-4 w-4" />}
                title="Export sessions"
                aria-label="Export sessions"
                onClick={() => setOpen((v) => !v)}
            />
            <Popover open={open} onClose={() => setOpen(false)} anchorEl={anchorRef.current} placement="bottom-end" className="w-64 space-y-3 p-3">
                <div className="text-sm font-medium text-slate-900 dark:text-slate-100">Export sessions</div>
                <DateRangePicker label="Days (UTC)" value={range} onChange={setRange} maxDate={new Date()} />
                <SegmentedControl
                    aria-label="Export format"
                    size="sm"
                    fullWidth
                    value={format}
                    onChange={setFormat}
                    options={[
                        { value: 'jsonl', label: 'JSONL', title: 'One session per line with the full transcript' },
                        { value: 'csv', label: 'CSV', title: 'One summary row per session' },
                    ]}
                />
                <Button size="sm" fullWidth loading={downloading} disabled={!range.start} onClick={onExport}>
                    Download
                </Button>
            </Popover>
        </>
    )
}
